import React, { useEffect, useState, useRef } from 'react';
import { Image } from '@/components/common/image';
import { useTranslation } from 'react-i18next';
import { IconButton } from '../common/icon-button';
import { ArrowLeftCircleIcon, ArrowRightCircleIcon, Check } from 'lucide-react';
import { audioControllerHooks } from '@/manager';
import { AudioPlayState } from '@/manager/manager/audio-controller-manager';
import { useToast } from '@/hooks/use-toast';
import { ProgressSlider } from '../common/progress-slider';
import { formatAudioTime } from '@/utils/format-audio-time';
import VolumeControl from './volume-control';
import { cn } from '@/lib/utils';
import { Back15sIcon, Forward15sIcon, PauseIcon, PlayIcon } from '../agent-dashboard-content-management/assets';
import { AgentV2PushContentDetailData } from '@/services/agent-v2-push-content-detail';

export const LeftPodcast: React.FC<{ details: AgentV2PushContentDetailData }> = ({ details }) => {
  const { t } = useTranslation();

  const [audioUrl, setAudioUrl] = useState(details?.audioUrl || '');
  const { toast } = useToast();
  const [selectCurrentTime, setSelectCurrentTime] = useState(0);
  const [selectDuration, setSelectDuration] = useState(0);

  const wasPlayingBeforeSeekRef = useRef(false);
  // 倍速控制相关状态
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);
  const speedButtonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setAudioUrl(details?.audioUrl || '');
  }, [details]);

  // 倍速选项
  const speedOptions = [
    { value: 2, label: `2 ${t('agent_dashboard.content_management.detail_modal.left_podcast.rate')}` },
    { value: 1.5, label: `1.5 ${t('agent_dashboard.content_management.detail_modal.left_podcast.rate')}` },
    { value: 1.25, label: `1.25 ${t('agent_dashboard.content_management.detail_modal.left_podcast.rate')}` },
    { value: 1, label: `1 ${t('agent_dashboard.content_management.detail_modal.left_podcast.rate')}` },
    { value: 0.75, label: `0.75 ${t('agent_dashboard.content_management.detail_modal.left_podcast.rate')}` },
  ];

  const {
    playState,
    duration,
    currentTime,
    playbackRate,
    volume,
    muted,
    play,
    pause,
    stop,
    seekTo,
    setPlaybackRate: setPlaybackRateValue,
    startPlaybackRateDrag,
    endPlaybackRateDrag,
    setPlaybackRateWhileDragging,
    setVolume,
    setMuted,
  } = audioControllerHooks.useController()

  const isLoading = playState === AudioPlayState.LOADING;
  const isPlaying = playState === AudioPlayState.PLAYING;
  const isEnd = playState === AudioPlayState.ENDED;

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();

    try {
      isPlaying ? pause() : await play(audioUrl);
    } catch (error) {
      stop();
      toast.default({
        title: "Play audio failed",
        description: "Play audio failed, please try again.",
      });
      console.error("Play audio failed", error);
    }
  };

  // 后退15秒
  const handleBack15s = () => {
    const targetTime = Math.max(0, currentTime - 15);

    if (isEnd) {
      if (targetTime !== 0) {
        // 播放结束状态且目标时间不等于0，跳转到目标位置并开始播放
        seekTo(targetTime);
        play(audioUrl);
      } else {
        // 播放结束状态且目标时间等于0，直接重新播放
        play(audioUrl);
      }
    } else {
      // 正常播放状态下跳转到目标位置
      seekTo(targetTime);
    }
  }

  // 前进15秒
  const handleForward15s = () => {
    seekTo(Math.min(selectDuration, currentTime + 15));
  }

  const handleSeekDragStart = () => {
    wasPlayingBeforeSeekRef.current = isPlaying;
    if (isPlaying) {
      pause();
    }
  };

  const handleSeekDragEnd = async () => {
    if (!audioUrl || !wasPlayingBeforeSeekRef.current) {
      wasPlayingBeforeSeekRef.current = false;
      return;
    }

    try {
      await play(audioUrl);
    } catch (error) {
      stop();
      console.error("Resume audio failed", error);
    } finally {
      wasPlayingBeforeSeekRef.current = false;
    }
  };


  // 处理倍速选择
  const handleSpeedSelect = (speed: number) => {
    setPlaybackRateValue(speed);
    setShowSpeedMenu(false);
  }

  // 处理倍速按钮点击
  const handleSpeedButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowSpeedMenu(!showSpeedMenu);
  }

  // 点击外部关闭倍速菜单
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (speedButtonRef.current && !speedButtonRef.current.contains(event.target as Node)) {
        setShowSpeedMenu(false);
      }
    };

    if (showSpeedMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSpeedMenu]);

  // 同步选中音频播放进度
  useEffect(() => {
    setSelectCurrentTime(currentTime);
  }, [currentTime]);

  useEffect(() => {
    setSelectDuration(duration);
  }, [duration]);

  useEffect(() => {
    play(audioUrl);
  }, []);

  return (
    <div className='flex-1 h-full relative bg-white flex flex-col border-r border-r-[rgba(233,234,235,1)] overflow-hidden'>
      {/* 遮罩层：增加黑色透明度，确保白色文字在亮色封面上也能看清 */}
      <div className='absolute inset-0 z-0 bg-black/60'>
        {/* 背景图片层：使用 coverImageUrl 并添加高斯模糊 */}
        {details?.coverImageUrl && (
          <Image
            className="absolute inset-0 z-0 bg-cover bg-center bg-no-repeat blur-2xl opacity-60 object-cover w-full h-full"
            src={details?.coverImageUrl || ''}
          />
        )}
      </div>
      {/* 播放器内容容器*/}
      <div className='relative flex flex-col items-center justify-center p-8 h-full'>
        <div className='w-[255px] h-[256px] rounded-[16px] [box-shadow:0px_0px_12px_3px_rgba(16,_24,_40,_0.06),_0px_8px_16px_-4px_rgba(16,_24,_40,_0.08)] mb-[60px]'>
          <Image
            src={details?.coverImageUrl || ''}
            alt='Podcast Cover'
            className='w-full h-full rounded-[16px] object-cover'
          />
        </div>
        {/* 标题 */}
        <div className='w-[319px]'>
          <p className='text-white text-[17px] font-medium leading-[25px]'>
            {details?.contentTitle || ''}
          </p>
        </div>
        {/* 进度条 */}
        <div className='w-[319px] select-none mt-5'>
          <ProgressSlider
            value={selectCurrentTime}
            max={selectDuration}
            trackColor="rgba(16,24,40,0.1)"
            progressColor={audioUrl ? "rgba(255,255,255,1)" : "rgb(234,236,240)"}
            thumbColor="rgb(255,255,255)"
            thumbBorderColor="rgba(255,255,255,1)"
            onChange={seekTo}
            onDragStart={handleSeekDragStart}
            onDragEnd={handleSeekDragEnd}
            disabled={playState === AudioPlayState.LOADING || !audioUrl}
            className="flex-1 "
          />
          <div className='flex justify-between text-xs text-white text-opacity-80 mt-2'>
            <span>{formatAudioTime(selectCurrentTime)}</span>
            <span>{formatAudioTime(selectDuration)}</span>
          </div>
        </div>
        {/* 控制按钮 space-evenly */}
        <div className='relative w-[325px] flex items-center justify-between mt-10 text-white select-none' ref={speedButtonRef}>
          <div>
            <VolumeControl
              initialVolume={volume * 100}
              initialMuted={muted}
              onVolumeChange={(newVolume) => {
                // 调用音频控制器的方法来设置音量
                setVolume(newVolume);
              }}
              onMutedChange={(newMuted) => {
                // 调用音频控制器的方法来设置静音状态
                setMuted(newMuted);
              }}
            />
          </div>
          <div>
            <IconButton
              className='hover:bg-[#eaecf062] size-7'
              onClick={handleBack15s}
              icon={<Back15sIcon
                className='size-7 cursor-pointer'
              />}
            />
          </div>
          <div className='size-11 cursor-pointer'>
            {isPlaying ? (
              <div
                onClick={handleClick}
              >
                <PauseIcon
                  className='size-11'
                />
              </div>
            ) : (
              <div
                onClick={handleClick}
              >
                <PlayIcon
                  className='size-11'
                />
              </div>
            )}
          </div>
          <div>
            <IconButton
              className='hover:bg-[#eaecf062] size-7'
              onClick={handleForward15s}
              icon={<Forward15sIcon
                className='size-7 cursor-pointer'
              />}
            />
          </div>
          <div className='w-8'></div>
          {/* 倍速控制按钮 */}
          <div className='absolute top-1/2 transform -translate-y-1/2 right-0'>
            <div className='' >
              <div className='text-center'>
                <div
                  className='max-w-max text-[15px] font-medium transition-colors cursor-pointer rounded-[4px] p-0.5 hover:bg-[#eaecf062]'
                  onClick={handleSpeedButtonClick}
                >
                  <span className='mr-1'>{playbackRate}</span>
                  <span>{t('agent_dashboard.content_management.detail_modal.left_podcast.rate')}</span>
                </div>
              </div>
            </div>
          </div>
          {/* 倍速选择弹窗 */}
          {<div
            className={cn(
              "absolute bottom-full -right-[76px] transform -translate-x-1/2 mb-1 transition-all duration-200 ease-in-out",
              showSpeedMenu
                ? 'opacity-100 translate-y-0 pointer-events-auto'
                : 'opacity-0 translate-y-2 pointer-events-none'
            )}
          >
            <div className='bg-white rounded-lg shadow-lg border border-gray-200 p-2 min-w-[150px]'>
              {speedOptions.map((option) => (
                <div
                  key={option.value}
                  className={cn(
                    "px-3 py-2 text-sm font-medium border-b border-b-gray-100 last:border-b-0 cursor-pointer transition-colors hover:bg-gray-50 flex items-center justify-between text-gray-700",
                  )}
                  onClick={() => handleSpeedSelect(option.value)}
                >
                  <span>{option.label}</span>
                  <Check
                    className={cn(
                      "float-right inline-block size-5",
                      playbackRate === option.value ? "visible" : "invisible"
                    )}
                  />
                </div>
              ))}
            </div>
          </div>}
        </div>
      </div>
    </div>
  );
};